import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { jwtDecode } from "jwt-decode";
import { selectUserInfo, selectIsRestoringSession, logout } from "../../store/authUserSlice";
import { useLocation } from "react-router-dom";

/**
 * PrivateRoute - Protects routes by validating JWT token client-side
 *
 * PERFORMANCE OPTIMIZATION:
 * - Uses client-side JWT decoding (no API call needed)
 * - Instant validation (< 1ms vs 100-300ms API call)
 * - Only checks token structure and expiration
 * - Token refresh handled by Axios interceptor on 401 errors
 *
 * RACE CONDITION FIX:
 * - Waits for session restoration to complete before checking token
 * - Prevents logout during async token refresh on page load
 */
const PrivateRoute = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector(selectUserInfo);
  const isRestoringSession = useSelector(selectIsRestoringSession);
  const [isValid, setIsValid] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  var redirectUrl = queryParams.get("redirectUrl") || "/dashboard";

  if (redirectUrl && redirectUrl !== null && !redirectUrl.startsWith("/")) {
    redirectUrl = "/" + redirectUrl;
  }


  useEffect(() => {
    // CRITICAL: Wait for session restoration to complete
    // This prevents race condition where we check for token before it's restored
    if (isRestoringSession) {
      return; // Don't check yet, restoration in progress
    }

    const token = userInfo?.token;

    // No token → redirect to signin
    // NOTE: We don't clear localStorage here anymore
    // The logout/clearSession actions handle storage cleanup
    if (!token) {
      localStorage.setItem("redirectUrl", redirectUrl || "/");
      navigate("/signin", { replace: true });
      return;
    }

    // CLIENT-SIDE JWT validation (no API call!)
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000; // Convert to seconds

      // Check if token is expired
      if (decoded.exp < currentTime) {
        // Token expired → logout and redirect to signin
        dispatch(logout());
        navigate("/signin", { replace: true });
        return;
      }

      // Token is valid → allow access
      setIsValid(true);
    } catch (error) {
      // Invalid token → logout and redirect to signin
      dispatch(logout());
      navigate("/signin", { replace: true });
    }
  }, [userInfo?.token, isRestoringSession, navigate, dispatch]);

  // Show children only if token is valid
  // No loader needed - validation is instant (client-side)
  return isValid ? children : null;
};

export default PrivateRoute;
